import { getReservationsPartnerSuggestions } from '@/api/generated/reservations/reservations';
import type { ListRequest, PartnerSuggestion } from '@/api/generated/schemas';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  toListRequest,
} from '@/shared/ui/async-selector';

/**
 * *Ref model partnera — minimální tvar držený ve stavu (a ukládaný jako display data
 * selector filtrů ListPage). Identitou je stabilní Party id.
 */
export interface PartnerRef {
  partyId: number;
  name: string;
  kind: 'Person' | 'Organization';
}

/**
 * Selector partnerů (osoby + organizace přes Party identitu) nad
 * `GET /api/reservations/partners` — používá ho filtr „Zákazník" v ListPage obchodních
 * případů; stejná definice poslouží i formulářům.
 */
export const partnerSelector = defineEntityAsyncSelector<
  PartnerRef,
  PartnerSuggestion,
  ListRequest
>({
  api: {
    fetch: getReservationsPartnerSuggestions,
    buildApiParams: (ctx) => toListRequest(ctx.search, ctx.ids),
    queryKey: (request) => listRequestQueryKey('reservations.partners', request),
    mapItem: (dto) => ({
      partyId: dto.partyId,
      name: dto.name,
      kind: dto.kind,
    }),
  },
  key: (item) => item.partyId,
  display: {
    kind: 'nameCode',
    name: (item) => item.name,
  },
  columns: (t) => [
    {
      id: 'name',
      header: t('partnerSelector.cols.name'),
      sortable: true,
      cell: (item: PartnerRef) => item.name,
      flex: '2',
    },
    {
      id: 'kind',
      header: t('partnerSelector.cols.kind'),
      muted: true,
      cell: (item: PartnerRef) =>
        item.kind === 'Person'
          ? t('partnerSelector.kind.person')
          : t('partnerSelector.kind.organization'),
      flex: '1',
    },
  ],
  i18n: { namespace: 'reservations' },
});

export const PartnerSelector = partnerSelector.Selector;
